import { z } from 'zod';

/**
 * api#388 ruling B. Populated only when `reused` is true — see the field's
 * own doc comment below for what its `intent_id` does and does not match.
 */
const refundPriorRefundSchema = z.object({
  intent_id: z.string().min(1),
  amount_cents: z.number().int().safe().positive(),
  created_at: z.number(),
});

const refundOrderResultSchema = z
  .object({
    status: z.enum(['prepared', 'completed', 'reconciliation_required']),
    intent_id: z.string().min(1),
    amount_cents: z.number().int().safe().positive(),
    reconciliation_required: z.boolean(),
    label_failures: z.number().int().safe().nonnegative(),
    payout_holds: z.array(z.string().min(1)),
    tax_reporting_status: z.enum([
      'not_required',
      'pending_policy',
      'succeeded',
      'failed',
    ]),
    // api#388 ruling B/D2 (refundOrder.ts:186-214 in gearfocus-api).
    //
    // ⛔ PRE-DEPLOY COMPATIBILITY, LOAD-BEARING: `.default()` on all three.
    // The web PR and the function deploy are separate steps by separate
    // mechanisms — this web PR can merge and go live BEFORE the api function
    // that adds these fields is deployed. If they were required, the
    // currently-deployed server's response (which lacks all three) would
    // fail this schema and EVERY refund, including fully successful ones,
    // would show the operator "needs reconciliation... do not create a new
    // refund" while the money really moved — the exact bug class this
    // branch already fixed once (the composite-intent-id defect), now
    // arriving from the opposite direction. Verified by feeding the exact
    // pre-deploy response shape through parseRefundOrderResult: see
    // "pre-deploy compatibility" in orders.service.refundV2.contract.test.ts.
    //
    // Defaulting cannot soften an alarm: an old-server PARTIAL refund still
    // reports non-empty `payout_holds` and `reconciliation_required: true`,
    // and a defaulted empty `payout_hold_reasons` makes the client's
    // `isDesignedPayoutPark` (RefundBuyerModal.tsx) false via its own
    // `.length > 0` guard — so it stays the RED "needs reconciliation" path,
    // exactly as production behaves today. Defaulting `reused: false` cannot
    // cause a double refund either: the duplicate guard is entirely
    // server-side (`prepare()` returns the prior intent regardless of what
    // the client believes) — the client's `reused` handling only decides
    // what the operator is TOLD, never what money moves.
    reused: z.boolean().default(false),
    prior_refund: refundPriorRefundSchema.nullable().default(null),
    // D2's vocabulary, NOT item.payout_hold_reason's — see the long note on
    // isDesignedPayoutPark in RefundBuyerModal.tsx. Deliberately not an enum:
    // an unrecognized value must still parse (and then fail RED on the
    // client, per D2), not be rejected here as a malformed response.
    payout_hold_reasons: z.array(z.string().min(1)).default([]),
    // api#388 ruling 1 (Bill, 2026-08-21 "Approve all + both add-ons"). Keyed
    // `${seller_id}/${item_id}` (functions/src/orders/utils/refundOrder.ts:1056)
    // — the item's true cumulative refunded cents, computed server-side
    // inside the same transaction as the refund itself. This is what lets
    // RefundBuyerModal show an AUTHORITATIVE running total instead of the
    // stale-prop-plus-arithmetic figure a reviewer showed could be wrong on
    // a money screen (gate round 2 item 7). `.default({})` for the same
    // pre-deploy-compatibility reason as `payout_hold_reasons` above — a
    // server response that predates this field must not throw, and a
    // missing entry degrades to no running-total clause rather than a
    // fabricated number (see RefundBuyerModal.tsx's designedPark toast).
    item_refund_totals_cents: z.record(z.string(), z.number()).default({}),
  })
  .strip()
  .superRefine((result, context) => {
    const followUpRequired =
      result.status !== 'completed' ||
      result.label_failures > 0 ||
      result.payout_holds.length > 0 ||
      result.tax_reporting_status === 'pending_policy' ||
      result.tax_reporting_status === 'failed';

    if (followUpRequired && !result.reconciliation_required) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reconciliation_required'],
        message:
          'A refund result with incomplete or failed follow-up work must require reconciliation.',
      });
    }

    // api#388 ruling B: a server that sets `reused` without saying what it
    // reused is a malformed response, not a refund we can safely tell the
    // operator was "already issued on undefined."
    if (result.reused && result.prior_refund === null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['prior_refund'],
        message: 'A reused refund result must name the prior refund.',
      });
    }

    // Codex gate (api#388, item 10): this must hold for EVERY reused
    // result, not only when `intent_id` happens to mismatch the caller's
    // expectation. Without it, a self-inconsistent response —
    // `{ reused: true, intent_id: <the id the caller expected>, prior_refund:
    // { intent_id: <a DIFFERENT id> } }` — parsed clean, because
    // `parseRefundOrderResult` only ran its consistency check inside the
    // `intent_id !== expectedIntentId` branch. That response claims the
    // EXPECTED intent was reused while naming a different one as the prior
    // refund; the modal would show that OTHER intent's amount and date, and
    // — worse — send that id as `allow_duplicate_of`, a field whose entire
    // purpose is to authorize a REAL second refund. An operator would be
    // acknowledging a refund they were never shown. Requiring this here,
    // unconditionally whenever `reused` is true, is the same class of rule
    // as the "reused implies prior_refund" check just above — both are
    // about `prior_refund`'s own internal coherence, not about whether it
    // matches what the CALLER expected, which is `parseRefundOrderResult`'s
    // job below.
    if (
      result.reused &&
      result.prior_refund !== null &&
      result.intent_id !== result.prior_refund.intent_id
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['intent_id'],
        message:
          'A reused refund result must name itself as the prior refund it reused.',
      });
    }
  });

export type RefundOrderResult = z.infer<typeof refundOrderResultSchema>;
export type RefundPriorRefund = z.infer<typeof refundPriorRefundSchema>;

export const REFUND_ORDER_RESULT_AMBIGUOUS_MESSAGE =
  'Refund response needs reconciliation. Reuse this intent or contact support; do not create a new refund.';

export class RefundOrderResultContractError extends Error {
  readonly code = 'refund-result-contract-invalid';

  constructor() {
    super(REFUND_ORDER_RESULT_AMBIGUOUS_MESSAGE);
    this.name = 'RefundOrderResultContractError';
  }
}

/**
 * Treats a malformed callable response as an ambiguous money outcome. The
 * stable error deliberately carries no pre-Stripe proof, so the browser keeps
 * and reuses the durable intent instead of minting a second refund request.
 */
/**
 * The server does NOT echo back the `intent_id` the client sent. It returns the
 * Firestore document id of the durable intent, which it derives by joining the
 * order id and the client's intent id:
 *
 *   `refundIntentDocumentId` — functions/src/orders/utils/refundOrder.ts:344-346
 *     `${request.order_id}_${request.intent_id}`
 *
 * That composite is what is stored as `intent.id` (refundOrder.ts:946), what is
 * returned as `intent_id` (refundOrder.ts:1299), and what is stamped into the
 * Stripe refund's `metadata.refund_intent_id` (index.ts:3028-3031).
 *
 * Comparing the response against the RAW client intent id therefore never
 * matches, and every refund — including completely successful ones — is
 * misread as an ambiguous outcome. Callers must build the expected id with
 * this helper so the identity check actually checks identity.
 */
export const refundIntentDocumentId = (
  orderId: string,
  intentId: string
): string => `${orderId}_${intentId}`;

export const parseRefundOrderResult = (
  value: unknown,
  expectedIntentId: string
): RefundOrderResult => {
  const parsed = refundOrderResultSchema.safeParse(value);
  if (!parsed.success) throw new RefundOrderResultContractError();
  const result = parsed.data;
  if (result.intent_id !== expectedIntentId) {
    // api#388 ruling B2: a fresh intent id whose request turned out to be
    // equivalent to an existing terminal intent gets back that PRIOR intent's
    // id, which by construction is not `expectedIntentId` — the composite the
    // caller derived from ITS OWN request. That mismatch is not the "response
    // and request disagree about which refund this is" conflict this check
    // exists to catch; it's the server correctly reporting a different,
    // already-resolved refund. Accept it only when the server says this was
    // reused — the schema's own superRefine (Codex gate, item 10) already
    // guarantees that whenever `reused` is true, `prior_refund` is present
    // AND `intent_id === prior_refund.intent_id`, so there is nothing left
    // to re-check about internal coherence here. This function's own job is
    // narrower: identity versus what the CALLER expected. Any mismatch
    // without `reused` is still the genuine conflict it always was and
    // still throws.
    if (!result.reused) {
      throw new RefundOrderResultContractError();
    }
  }
  return result;
};

/**
 * web#388 item 4 (web half) — `releaseRefundPayoutHold`'s response contract.
 *
 * Unlike `refundOrderResultSchema` above, a malformed response here is not a
 * money-ambiguity: `releaseRefundPayoutHold` never moves money (see the long
 * note on `REFUND_COPY` in refundCopy.ts — it clears a hold, nothing more),
 * so there is no "do not retry" hazard to protect against. A bad shape is
 * just an error the caller surfaces through the generic
 * `RELEASE_PAYOUT_HOLD_GENERIC_ERROR_MESSAGE`, same as any other unexpected
 * failure on this action.
 *
 * ⛔ CPO adversarial gate (api#388 item 4, CRITICAL, caught before merge):
 * `intent_id` / `prior_hold_reason` / `released_at` / `released_by` are
 * `string | null` / `number | null` on the SERVER's own declared type
 * (gearfocus-api `releaseRefundPayoutHold.ts:1986-1996`,
 * `ReleaseRefundPayoutHoldResult`), not always-present. The server sends all
 * four as `null` on its idempotent already-released branch (a second release
 * call, or a call against an item that never had a hold — refundOrder.ts
 * :2118-2128), and sends `intent_id: null` even on a GENUINE success when
 * the item's `payout_hold_ref` was missing or dangling (the "no owning
 * intent" legacy-hold path — refundOrder.ts:2215-2236, `intentId` stays
 * `null` unless `intentRef && intentSnapshot?.exists`). Declaring these
 * non-nullable made `parseReleaseRefundPayoutHoldResult` throw on two of the
 * three shapes the server actually sends — the operator saw the generic
 * "could not be cleared, try again or contact support" message with ZERO
 * query invalidations, on a call where the server had genuinely cleared the
 * hold. See `orderDetailItemMarkPayoutHandled.test.tsx` for the mutation
 * proof (revert `.nullable()` here and the already-released / legacy-hold
 * tests fail) and the verbatim server-shaped fixtures.
 */
const releaseRefundPayoutHoldResultSchema = z
  .object({
    released: z.boolean(),
    already_released: z.boolean(),
    order_id: z.string().min(1),
    seller_id: z.string().min(1),
    item_id: z.string().min(1),
    intent_id: z.string().min(1).nullable(),
    prior_hold_reason: z.string().min(1).nullable(),
    released_at: z.number().nullable(),
    released_by: z.string().min(1).nullable(),
  })
  .strip();

export type ReleaseRefundPayoutHoldResult = z.infer<
  typeof releaseRefundPayoutHoldResultSchema
>;

export class ReleaseRefundPayoutHoldResultContractError extends Error {
  readonly code = 'release-payout-hold-result-contract-invalid';

  constructor() {
    super('releaseRefundPayoutHold returned an unexpected shape.');
    this.name = 'ReleaseRefundPayoutHoldResultContractError';
  }
}

export const parseReleaseRefundPayoutHoldResult = (
  value: unknown
): ReleaseRefundPayoutHoldResult => {
  const parsed = releaseRefundPayoutHoldResultSchema.safeParse(value);
  if (!parsed.success) throw new ReleaseRefundPayoutHoldResultContractError();
  return parsed.data;
};
